import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { URL } from "../AxiosUrlGlobal";
import "./Attack.css";

// Security events recorded by the backend (utils/Security/securityEvents.js)
const EVENT_TYPES = {
  "security:signup_blocked_off": { label: "Blocked (sign-up off)", color: "#455a64", icon: "public_off" },
  "security:intl_signup_switched": { label: "Sign-up switched", color: "#00838f", icon: "toggle_on" },
  "security:signup_blocked_vpn": { label: "VPN / datacenter blocked", color: "#d32f2f", icon: "vpn_lock" },
  "security:signup_blocked_ip": { label: "IP limit blocked", color: "#e64a19", icon: "block" },
  "security:intl_signup": { label: "International sign-up", color: "#2e7d32", icon: "person_add" },
  "security:otp_sent": { label: "OTP SMS sent", color: "#1565c0", icon: "sms" },
  "security:otp_blocked": { label: "OTP blocked (3/day)", color: "#ef6c00", icon: "sms_failed" },
  "security:otp_limit": { label: "Resend limit hit", color: "#f9a825", icon: "timer_off" },
  "security:twilio_error": { label: "Twilio error", color: "#8d6e63", icon: "error" },
  "security:fake_account_deleted": { label: "Fake account deleted", color: "#6a1b9a", icon: "person_remove" },
};

const TWILIO_CODES = {
  60410: "Number blocked by Twilio for fraud",
  20003: "Twilio account suspended",
};

const typeInfo = (type) => EVENT_TYPES[type] || { label: type, color: "#757575", icon: "info" };

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZone: "Asia/Dhaka",
      })
    : "-";

const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return "-";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};

const IpLink = ({ ip }) =>
  ip ? (
    <a className="atk-ip" href={`https://ipinfo.io/${ip}`} target="_blank" rel="noreferrer" title="Look up this IP">
      {ip}
    </a>
  ) : (
    <span className="atk-muted">-</span>
  );

const TypeBadge = ({ type }) => {
  const info = typeInfo(type);
  return (
    <span className="atk-badge" style={{ backgroundColor: info.color }}>
      <span className="material-symbols-outlined">{info.icon}</span>
      {info.label}
    </span>
  );
};

function Attack() {
  const [range, setRange] = useState("24h");
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      const uid = localStorage.getItem("id");
      const response = await axios.post(
        `${URL}/security/attack-overview`,
        { range, type, search: appliedSearch, page, limit: 50 },
        { headers: { token, uid } }
      );
      if (response.data?.success) {
        setData(response.data.data);
        setUpdatedAt(new Date());
      } else {
        setError(response.data?.error || "Could not load attack data");
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [range, type, page, appliedSearch]);

  useEffect(() => {
    fetchOverview();
    const timer = setInterval(fetchOverview, 60000);
    return () => clearInterval(timer);
  }, [fetchOverview]);

  // The search box applies on submit, not on every keystroke
  const [switching, setSwitching] = useState(false);
  const intlEnabled = data?.internationalSignupEnabled !== false;

  const toggleInternationalSignup = async () => {
    const enable = !intlEnabled;
    const question = enable
      ? "Turn international sign-up back ON?"
      : "Turn international sign-up OFF?\n\nNobody can sign up with a non-Bangladeshi number until you turn it back on. Bangladeshi numbers are not affected.";
    if (!window.confirm(question)) return;
    try {
      setSwitching(true);
      const token = localStorage.getItem("token");
      const uid = localStorage.getItem("id");
      const response = await axios.put(
        `${URL}/app-config/${uid}`,
        { internationalSignup: { enabled: enable } },
        { headers: { token, uid } }
      );
      if (!response.data?.success) {
        window.alert(response.data?.error || "Could not change the setting");
      }
      await fetchOverview();
    } catch (err) {
      window.alert(err.response?.data?.error || err.message);
    } finally {
      setSwitching(false);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    if (search.trim() === appliedSearch) fetchOverview();
    else setAppliedSearch(search.trim());
  };

  const summary = data?.summary || {};
  const count = (t) => summary[t] || 0;
  const twilio = data?.twilio || {};
  const timeline = data?.timeline || [];
  const maxBucket = Math.max(1, ...timeline.map((b) => b.total));
  const pagination = data?.events?.pagination;

  return (
    <div className="atk-container">
      <div className="atk-header">
        <div>
          <h1>Attack Monitor</h1>
          <p>
            Sign-up and OTP abuse on international numbers
            {updatedAt && <span className="atk-muted"> · updated {formatDate(updatedAt)} · refreshes every minute</span>}
          </p>
        </div>
        <div className="atk-header-actions">
          {["24h", "7d", "30d"].map((r) => (
            <button
              key={r}
              className={`atk-range ${range === r ? "active" : ""}`}
              onClick={() => {
                setRange(r);
                setPage(1);
              }}
            >
              {r}
            </button>
          ))}
          <button className="atk-refresh" onClick={fetchOverview} disabled={loading} title="Refresh">
            <span className="material-symbols-outlined">refresh</span>
          </button>
        </div>
      </div>

      {error && <div className="atk-error">{error}</div>}

      {/* Status: sign-up switch, Twilio, VPN list, active rules */}
      <div className="atk-status-row">
        <div className={`atk-card atk-switch-card ${intlEnabled ? "ok" : "bad"}`}>
          <span className="material-symbols-outlined">{intlEnabled ? "public" : "public_off"}</span>
          <div>
            <small>International sign-up</small>
            <strong>{data ? (intlEnabled ? "ON" : "OFF") : "…"}</strong>
            <span>
              {intlEnabled
                ? "Non-Bangladeshi numbers can sign up (rules below apply)"
                : "All sign-ups with non-Bangladeshi numbers are refused"}
            </span>
            <button
              className={`atk-switch ${intlEnabled ? "on" : "off"}`}
              onClick={toggleInternationalSignup}
              disabled={!data || switching}
            >
              {switching ? "Saving…" : intlEnabled ? "Turn OFF" : "Turn ON"}
            </button>
          </div>
        </div>
        <div className={`atk-card atk-twilio ${twilio.status === "active" ? "ok" : "bad"}`}>
          <span className="material-symbols-outlined">sms</span>
          <div>
            <small>Twilio (international OTP)</small>
            <strong>{twilio.status ? twilio.status.toUpperCase() : "…"}</strong>
            {twilio.balance !== undefined && (
              <span>
                Balance: {Number(twilio.balance).toFixed(2)} {twilio.currency}
              </span>
            )}
            {twilio.status === "suspended" && <span>Top up and reactivate in the Twilio Console</span>}
          </div>
        </div>
        <div className={`atk-card ${data?.ipList?.loaded ? "ok" : "bad"}`}>
          <span className="material-symbols-outlined">vpn_lock</span>
          <div>
            <small>VPN / datacenter IP list</small>
            <strong>{data?.ipList?.loaded ? `${data.ipList.ranges.toLocaleString()} ranges` : "Not loaded"}</strong>
            {data?.ipList?.fetchedAt && <span>Updated {formatDate(data.ipList.fetchedAt)}</span>}
          </div>
        </div>
        <div className="atk-card atk-rules">
          <span className="material-symbols-outlined">shield</span>
          <div>
            <small>Active rules (international numbers)</small>
            <ul>
              <li>Sign-up from VPN / datacenter IP is blocked</li>
              <li>1 sign-up per IP per day</li>
              <li>3 OTPs per number per day (first + 2 resends)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Counters for the selected range */}
      <div className="atk-stats">
        {Object.keys(EVENT_TYPES).map((t) => {
          const info = typeInfo(t);
          return (
            <button
              key={t}
              className={`atk-stat ${type === t ? "selected" : ""}`}
              onClick={() => {
                setType(type === t ? "all" : t);
                setPage(1);
              }}
              title="Filter the event log"
            >
              <span className="material-symbols-outlined" style={{ color: info.color }}>
                {info.icon}
              </span>
              <div>
                <small>{info.label}</small>
                <strong>{count(t).toLocaleString()}</strong>
              </div>
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      <div className="atk-panel">
        <h3>Activity {range === "24h" ? "per hour" : "per day"} (Bangladesh time)</h3>
        {timeline.length === 0 ? (
          <div className="atk-empty-small">No security events in this period</div>
        ) : (
          <div className="atk-timeline">
            {timeline.map((b) => (
              <div
                key={b.bucket}
                className="atk-bar-wrap"
                title={`${b.bucket}\n${Object.entries(b.byType)
                  .map(([t, n]) => `${typeInfo(t).label}: ${n}`)
                  .join("\n")}`}
              >
                <div className="atk-bar" style={{ height: `${(b.total / maxBucket) * 100}%` }}>
                  {Object.entries(b.byType).map(([t, n]) => (
                    <div
                      key={t}
                      style={{ flexGrow: n, backgroundColor: typeInfo(t).color }}
                      className="atk-bar-part"
                    />
                  ))}
                </div>
                <span className="atk-bar-label">{range === "24h" ? b.bucket.slice(11, 13) : b.bucket.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="atk-two-col">
        {/* Top IPs */}
        <div className="atk-panel">
          <h3>Top IPs</h3>
          {!data?.topIps?.length ? (
            <div className="atk-empty-small">No IPs recorded in this period</div>
          ) : (
            <table className="atk-table">
              <thead>
                <tr>
                  <th>IP</th>
                  <th>Events</th>
                  <th>Network</th>
                  <th>What happened</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {data.topIps.map((row) => (
                  <tr key={row.ip}>
                    <td>
                      <IpLink ip={row.ip} />
                      {row.vpn && <span className="atk-vpn">VPN</span>}
                    </td>
                    <td>{row.count}</td>
                    <td className="atk-muted">{row.isp || "-"}</td>
                    <td>
                      {row.types.map((t) => (
                        <span key={t} className="atk-dot" style={{ backgroundColor: typeInfo(t).color }} title={typeInfo(t).label} />
                      ))}
                      {row.mobiles.length > 0 && <div className="atk-small">{row.mobiles.join(", ")}</div>}
                    </td>
                    <td className="atk-nowrap">{formatDate(row.lastSeen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Live limits */}
        <div className="atk-panel">
          <h3>Limits in effect right now</h3>
          <h4>IPs that used today's sign-up</h4>
          {!data?.activeLimits?.signupIps?.length ? (
            <div className="atk-empty-small">None</div>
          ) : (
            <table className="atk-table">
              <thead>
                <tr>
                  <th>IP</th>
                  <th>Sign-ups</th>
                  <th>Resets in</th>
                </tr>
              </thead>
              <tbody>
                {data.activeLimits.signupIps.map((row) => (
                  <tr key={row.ip}>
                    <td>
                      <IpLink ip={row.ip} />
                    </td>
                    <td>{row.signups} / 1</td>
                    <td>{formatDuration(row.resetsIn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <h4>OTP SMS per number today</h4>
          {!data?.activeLimits?.otpNumbers?.length ? (
            <div className="atk-empty-small">None</div>
          ) : (
            <table className="atk-table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Sent</th>
                  <th>Resets in</th>
                </tr>
              </thead>
              <tbody>
                {data.activeLimits.otpNumbers.map((row) => (
                  <tr key={row.mobile}>
                    <td className="atk-mono">{row.mobile}</td>
                    <td>
                      {Math.min(row.sent, 3)} / 3 {row.blocked && <span className="atk-blocked">limit reached</span>}
                    </td>
                    <td>{formatDuration(row.resetsIn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* International sign-ups */}
      <div className="atk-panel">
        <h3>International sign-ups ({data?.intlSignups?.length || 0})</h3>
        {!data?.intlSignups?.length ? (
          <div className="atk-empty-small">No international sign-ups in this period</div>
        ) : (
          <div className="atk-scroll">
            <table className="atk-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Name</th>
                  <th>Number</th>
                  <th title="Mobile verified / Photo / CV / Face verified">Progress</th>
                  <th>Device</th>
                  <th>Sign-up IP</th>
                </tr>
              </thead>
              <tbody>
                {data.intlSignups.map((u) => (
                  <tr key={u._id}>
                    <td className="atk-nowrap">{formatDate(u.createdAt)}</td>
                    <td>{u.name}</td>
                    <td className="atk-mono">{u.mobile}</td>
                    <td>
                      {[
                        ["verified", "Mobile verified"],
                        ["photo", "Photo"],
                        ["cv", "CV"],
                        ["faceVerified", "Face verified"],
                      ].map(([key, label]) => (
                        <span key={key} className={`atk-check ${u[key] ? "yes" : "no"}`} title={label}>
                          {label[0]}
                        </span>
                      ))}
                    </td>
                    <td className="atk-muted">{u.device || "-"}</td>
                    <td>
                      <IpLink ip={u.ip} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Event log */}
      <div className="atk-panel">
        <div className="atk-log-head">
          <h3>Event log</h3>
          <form className="atk-filters" onSubmit={onSearch}>
            <select
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All events</option>
              {Object.entries(EVENT_TYPES).map(([t, info]) => (
                <option key={t} value={t}>
                  {info.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search IP, number or name"
            />
            <button type="submit">
              <span className="material-symbols-outlined">search</span>
            </button>
          </form>
        </div>

        {loading && !data ? (
          <div className="atk-empty-small">Loading…</div>
        ) : !data?.events?.items?.length ? (
          <div className="atk-empty-small">No events match</div>
        ) : (
          <div className="atk-scroll">
            <table className="atk-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Event</th>
                  <th>Details</th>
                  <th>IP</th>
                  <th>Number</th>
                </tr>
              </thead>
              <tbody>
                {data.events.items.map((e) => {
                  const m = e.metadata || {};
                  return (
                    <tr key={e._id}>
                      <td className="atk-nowrap">{formatDate(e.createdAt)}</td>
                      <td>
                        <TypeBadge type={e.type} />
                      </td>
                      <td className="atk-details">
                        {m.code && TWILIO_CODES[m.code] ? TWILIO_CODES[m.code] : e.details}
                        {m.name && <span className="atk-small"> · {m.name}</span>}
                        {m.isp && <span className="atk-small"> · {m.isp}</span>}
                        {m.vpn && <span className="atk-vpn">VPN</span>}
                        {m.code && <span className="atk-small"> · code {m.code}</span>}
                      </td>
                      <td>
                        <IpLink ip={m.ip} />
                      </td>
                      <td className="atk-mono">{m.mobile || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {pagination && pagination.total > 0 && (
          <div className="atk-pagination">
            <span>
              {(pagination.currentPage - 1) * pagination.limit + 1}–
              {Math.min(pagination.currentPage * pagination.limit, pagination.total)} of {pagination.total.toLocaleString()}
            </span>
            <div>
              <button onClick={() => setPage(1)} disabled={page === 1}>
                <span className="material-symbols-outlined">first_page</span>
              </button>
              <button onClick={() => setPage(page - 1)} disabled={page === 1}>
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <span className="atk-page">
                {pagination.currentPage} / {pagination.totalPages}
              </span>
              <button onClick={() => setPage(page + 1)} disabled={page >= pagination.totalPages}>
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
              <button onClick={() => setPage(pagination.totalPages)} disabled={page >= pagination.totalPages}>
                <span className="material-symbols-outlined">last_page</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Attack;
